/* ─── src/GLOBAL/Countries.ts ───
   All countries with ISO code, dial code, flag, and a link to their
   currency via currencyCode (matches CurrencyItem.code in Currencies.ts)
*/

import { CURRENCIES, CurrencyItem } from "./Currencies";

export interface CountryInfo {
  name: string;
  code: string;          // ISO 2-letter code
  phoneCode: string;     // e.g., "+254"
  emoji: string;         // 🇰🇪 for Kenya, etc.
  currencyCode: string;  // links to CurrencyItem.code in Currencies.ts
  continent?: Continent;
}

export type Continent =
  | "Africa"
  | "Asia"
  | "Europe"
  | "North America"
  | "South America"
  | "Oceania";

export const CONTINENTS: Continent[] = [
  "Africa", "Asia", "Europe", "North America", "South America", "Oceania",
];

const CONTINENT_BY_CODE: Record<string, Continent> = {
  AF: "Asia", AL: "Europe", DZ: "Africa", AD: "Europe", AO: "Africa",
  AG: "North America", AR: "South America", AM: "Asia", AU: "Oceania", AT: "Europe",
  AZ: "Asia", BS: "North America", BH: "Asia", BD: "Asia", BB: "North America",
  BY: "Europe", BE: "Europe", BZ: "North America", BJ: "Africa", BT: "Asia",
  BO: "South America", BA: "Europe", BW: "Africa", BR: "South America", BN: "Asia",
  BG: "Europe", BF: "Africa", BI: "Africa", KH: "Asia", CM: "Africa",
  CA: "North America", CV: "Africa", CF: "Africa", TD: "Africa", CL: "South America",
  CN: "Asia", CO: "South America", KM: "Africa", CG: "Africa", CD: "Africa",
  CR: "North America", HR: "Europe", CU: "North America", CY: "Europe", CZ: "Europe",
  DK: "Europe", DJ: "Africa", DM: "North America", DO: "North America", EC: "South America",
  EG: "Africa", SV: "North America", GQ: "Africa", ER: "Africa", EE: "Europe",
  SZ: "Africa", ET: "Africa", FJ: "Oceania", FI: "Europe", FR: "Europe",
  GA: "Africa", GM: "Africa", GE: "Asia", DE: "Europe", GH: "Africa",
  GR: "Europe", GD: "North America", GT: "North America", GN: "Africa", GW: "Africa",
  GY: "South America", HT: "North America", HN: "North America", HU: "Europe", IS: "Europe",
  IN: "Asia", ID: "Asia", IR: "Asia", IQ: "Asia", IE: "Europe",
  IL: "Asia", IT: "Europe", JM: "North America", JP: "Asia", JO: "Asia",
  KZ: "Asia", KE: "Africa", KI: "Oceania", KW: "Asia", KG: "Asia",
  LA: "Asia", LV: "Europe", LB: "Asia", LS: "Africa", LR: "Africa",
  LY: "Africa", LI: "Europe", LT: "Europe", LU: "Europe", MG: "Africa",
  MW: "Africa", MY: "Asia", MV: "Asia", ML: "Africa", MT: "Europe",
  MH: "Oceania", MR: "Africa", MU: "Africa", MX: "North America", FM: "Oceania",
  MD: "Europe", MC: "Europe", MN: "Asia", ME: "Europe", MA: "Africa",
  MZ: "Africa", MM: "Asia", NA: "Africa", NR: "Oceania", NP: "Asia",
  NL: "Europe", NZ: "Oceania", NI: "North America", NE: "Africa", NG: "Africa",
  KP: "Asia", MK: "Europe", NO: "Europe", OM: "Asia", PK: "Asia",
  PW: "Oceania", PS: "Asia", PA: "North America", PG: "Oceania", PY: "South America",
  PE: "South America", PH: "Asia", PL: "Europe", PT: "Europe", PR: "North America",
  QA: "Asia", RO: "Europe", RU: "Europe", RW: "Africa", KN: "North America",
  LC: "North America", VC: "North America", WS: "Oceania", SM: "Europe", ST: "Africa",
  SA: "Asia", SN: "Africa", RS: "Europe", SC: "Africa", SL: "Africa",
  SG: "Asia", SK: "Europe", SI: "Europe", SB: "Oceania", SO: "Africa",
  ZA: "Africa", KR: "Asia", SS: "Africa", ES: "Europe", LK: "Asia",
  SD: "Africa", SR: "South America", SE: "Europe", CH: "Europe", SY: "Asia",
  TW: "Asia", TJ: "Asia", TZ: "Africa", TH: "Asia", TG: "Africa",
  TO: "Oceania", TT: "North America", TN: "Africa", TR: "Asia", TM: "Asia",
  TV: "Oceania", UG: "Africa", UA: "Europe", AE: "Asia", GB: "Europe",
  US: "North America", UY: "South America", UZ: "Asia", VU: "Oceania", VA: "Europe",
  VE: "South America", VN: "Asia", YE: "Asia", ZM: "Africa", ZW: "Africa",
  // not in the list below, but Google's geocoder can return them
  CI: "Africa", XK: "Europe", HK: "Asia", MO: "Asia",
};

// names Google's geocoder returns that differ from the names in the list
const COUNTRY_NAME_ALIASES: Record<string, string> = {
  "czechia": "CZ",
  "türkiye": "TR",
  "turkiye": "TR",
  "myanmar (burma)": "MM",
  "the bahamas": "BS",
  "the gambia": "GM",
  "côte d'ivoire": "CI",
  "côte d’ivoire": "CI",
  "cote d'ivoire": "CI",
  "congo (drc)": "CD",
  "democratic republic of the congo": "CD",
  "republic of the congo": "CG",
  "bosnia & herzegovina": "BA",
  "kosovo": "XK",
  "hong kong": "HK",
  "macau": "MO",
  "usa": "US",
  "uk": "GB",
  "uae": "AE",
};

const Countries: CountryInfo[] = [
  { name: "Afghanistan", code: "AF", phoneCode: "+93", emoji: "🇦🇫", currencyCode: "AFN" },
  { name: "Albania", code: "AL", phoneCode: "+355", emoji: "🇦🇱", currencyCode: "ALL" },
  { name: "Algeria", code: "DZ", phoneCode: "+213", emoji: "🇩🇿", currencyCode: "DZD" },
  { name: "Andorra", code: "AD", phoneCode: "+376", emoji: "🇦🇩", currencyCode: "EUR" },
  { name: "Angola", code: "AO", phoneCode: "+244", emoji: "🇦🇴", currencyCode: "AOA" },
  { name: "Antigua and Barbuda", code: "AG", phoneCode: "+1-268", emoji: "🇦🇬", currencyCode: "XCD" },
  { name: "Argentina", code: "AR", phoneCode: "+54", emoji: "🇦🇷", currencyCode: "ARS" },
  { name: "Armenia", code: "AM", phoneCode: "+374", emoji: "🇦🇲", currencyCode: "AMD" },
  { name: "Australia", code: "AU", phoneCode: "+61", emoji: "🇦🇺", currencyCode: "AUD" },
  { name: "Austria", code: "AT", phoneCode: "+43", emoji: "🇦🇹", currencyCode: "EUR" },
  { name: "Azerbaijan", code: "AZ", phoneCode: "+994", emoji: "🇦🇿", currencyCode: "AZN" },
  { name: "Bahamas", code: "BS", phoneCode: "+1-242", emoji: "🇧🇸", currencyCode: "BSD" },
  { name: "Bahrain", code: "BH", phoneCode: "+973", emoji: "🇧🇭", currencyCode: "BHD" },
  { name: "Bangladesh", code: "BD", phoneCode: "+880", emoji: "🇧🇩", currencyCode: "BDT" },
  { name: "Barbados", code: "BB", phoneCode: "+1-246", emoji: "🇧🇧", currencyCode: "BBD" },
  { name: "Belarus", code: "BY", phoneCode: "+375", emoji: "🇧🇾", currencyCode: "BYN" },
  { name: "Belgium", code: "BE", phoneCode: "+32", emoji: "🇧🇪", currencyCode: "EUR" },
  { name: "Belize", code: "BZ", phoneCode: "+501", emoji: "🇧🇿", currencyCode: "BZD" },
  { name: "Benin", code: "BJ", phoneCode: "+229", emoji: "🇧🇯", currencyCode: "XOF" },
  { name: "Bhutan", code: "BT", phoneCode: "+975", emoji: "🇧🇹", currencyCode: "BTN" },
  { name: "Bolivia", code: "BO", phoneCode: "+591", emoji: "🇧🇴", currencyCode: "BOB" },
  { name: "Bosnia and Herzegovina", code: "BA", phoneCode: "+387", emoji: "🇧🇦", currencyCode: "BAM" },
  { name: "Botswana", code: "BW", phoneCode: "+267", emoji: "🇧🇼", currencyCode: "BWP" },
  { name: "Brazil", code: "BR", phoneCode: "+55", emoji: "🇧🇷", currencyCode: "BRL" },
  { name: "Brunei", code: "BN", phoneCode: "+673", emoji: "🇧🇳", currencyCode: "BND" },
  { name: "Bulgaria", code: "BG", phoneCode: "+359", emoji: "🇧🇬", currencyCode: "BGN" },
  { name: "Burkina Faso", code: "BF", phoneCode: "+226", emoji: "🇧🇫", currencyCode: "XOF" },
  { name: "Burundi", code: "BI", phoneCode: "+257", emoji: "🇧🇮", currencyCode: "BIF" },
  { name: "Cambodia", code: "KH", phoneCode: "+855", emoji: "🇰🇭", currencyCode: "KHR" },
  { name: "Cameroon", code: "CM", phoneCode: "+237", emoji: "🇨🇲", currencyCode: "XAF" },
  { name: "Canada", code: "CA", phoneCode: "+1", emoji: "🇨🇦", currencyCode: "CAD" },
  { name: "Cape Verde", code: "CV", phoneCode: "+238", emoji: "🇨🇻", currencyCode: "CVE" },
  { name: "Central African Republic", code: "CF", phoneCode: "+236", emoji: "🇨🇫", currencyCode: "XAF" },
  { name: "Chad", code: "TD", phoneCode: "+235", emoji: "🇹🇩", currencyCode: "XAF" },
  { name: "Chile", code: "CL", phoneCode: "+56", emoji: "🇨🇱", currencyCode: "CLP" },
  { name: "China", code: "CN", phoneCode: "+86", emoji: "🇨🇳", currencyCode: "CNY" },
  { name: "Colombia", code: "CO", phoneCode: "+57", emoji: "🇨🇴", currencyCode: "COP" },
  { name: "Comoros", code: "KM", phoneCode: "+269", emoji: "🇰🇲", currencyCode: "KMF" },
  { name: "Congo (Brazzaville)", code: "CG", phoneCode: "+242", emoji: "🇨🇬", currencyCode: "XAF" },
  { name: "Congo (Kinshasa)", code: "CD", phoneCode: "+243", emoji: "🇨🇩", currencyCode: "CDF" },
  { name: "Costa Rica", code: "CR", phoneCode: "+506", emoji: "🇨🇷", currencyCode: "CRC" },
  { name: "Croatia", code: "HR", phoneCode: "+385", emoji: "🇭🇷", currencyCode: "EUR" },
  { name: "Cuba", code: "CU", phoneCode: "+53", emoji: "🇨🇺", currencyCode: "CUP" },
  { name: "Cyprus", code: "CY", phoneCode: "+357", emoji: "🇨🇾", currencyCode: "EUR" },
  { name: "Czech Republic", code: "CZ", phoneCode: "+420", emoji: "🇨🇿", currencyCode: "CZK" },
  { name: "Denmark", code: "DK", phoneCode: "+45", emoji: "🇩🇰", currencyCode: "DKK" },
  { name: "Djibouti", code: "DJ", phoneCode: "+253", emoji: "🇩🇯", currencyCode: "DJF" },
  { name: "Dominica", code: "DM", phoneCode: "+1-767", emoji: "🇩🇲", currencyCode: "XCD" },
  { name: "Dominican Republic", code: "DO", phoneCode: "+1-809", emoji: "🇩🇴", currencyCode: "DOP" },
  { name: "Ecuador", code: "EC", phoneCode: "+593", emoji: "🇪🇨", currencyCode: "USD" },
  { name: "Egypt", code: "EG", phoneCode: "+20", emoji: "🇪🇬", currencyCode: "EGP" },
  { name: "El Salvador", code: "SV", phoneCode: "+503", emoji: "🇸🇻", currencyCode: "USD" },
  { name: "Equatorial Guinea", code: "GQ", phoneCode: "+240", emoji: "🇬🇶", currencyCode: "XAF" },
  { name: "Eritrea", code: "ER", phoneCode: "+291", emoji: "🇪🇷", currencyCode: "ERN" },
  { name: "Estonia", code: "EE", phoneCode: "+372", emoji: "🇪🇪", currencyCode: "EUR" },
  { name: "Eswatini", code: "SZ", phoneCode: "+268", emoji: "🇸🇿", currencyCode: "SZL" },
  { name: "Ethiopia", code: "ET", phoneCode: "+251", emoji: "🇪🇹", currencyCode: "ETB" },
  { name: "Fiji", code: "FJ", phoneCode: "+679", emoji: "🇫🇯", currencyCode: "FJD" },
  { name: "Finland", code: "FI", phoneCode: "+358", emoji: "🇫🇮", currencyCode: "EUR" },
  { name: "France", code: "FR", phoneCode: "+33", emoji: "🇫🇷", currencyCode: "EUR" },
  { name: "Gabon", code: "GA", phoneCode: "+241", emoji: "🇬🇦", currencyCode: "XAF" },
  { name: "Gambia", code: "GM", phoneCode: "+220", emoji: "🇬🇲", currencyCode: "GMD" },
  { name: "Georgia", code: "GE", phoneCode: "+995", emoji: "🇬🇪", currencyCode: "GEL" },
  { name: "Germany", code: "DE", phoneCode: "+49", emoji: "🇩🇪", currencyCode: "EUR" },
  { name: "Ghana", code: "GH", phoneCode: "+233", emoji: "🇬🇭", currencyCode: "GHS" },
  { name: "Greece", code: "GR", phoneCode: "+30", emoji: "🇬🇷", currencyCode: "EUR" },
  { name: "Grenada", code: "GD", phoneCode: "+1-473", emoji: "🇬🇩", currencyCode: "XCD" },
  { name: "Guatemala", code: "GT", phoneCode: "+502", emoji: "🇬🇹", currencyCode: "GTQ" },
  { name: "Guinea", code: "GN", phoneCode: "+224", emoji: "🇬🇳", currencyCode: "GNF" },
  { name: "Guinea-Bissau", code: "GW", phoneCode: "+245", emoji: "🇬🇼", currencyCode: "XOF" },
  { name: "Guyana", code: "GY", phoneCode: "+592", emoji: "🇬🇾", currencyCode: "GYD" },
  { name: "Haiti", code: "HT", phoneCode: "+509", emoji: "🇭🇹", currencyCode: "HTG" },
  { name: "Honduras", code: "HN", phoneCode: "+504", emoji: "🇭🇳", currencyCode: "HNL" },
  { name: "Hungary", code: "HU", phoneCode: "+36", emoji: "🇭🇺", currencyCode: "HUF" },
  { name: "Iceland", code: "IS", phoneCode: "+354", emoji: "🇮🇸", currencyCode: "ISK" },
  { name: "India", code: "IN", phoneCode: "+91", emoji: "🇮🇳", currencyCode: "INR" },
  { name: "Indonesia", code: "ID", phoneCode: "+62", emoji: "🇮🇩", currencyCode: "IDR" },
  { name: "Iran", code: "IR", phoneCode: "+98", emoji: "🇮🇷", currencyCode: "IRR" },
  { name: "Iraq", code: "IQ", phoneCode: "+964", emoji: "🇮🇶", currencyCode: "IQD" },
  { name: "Ireland", code: "IE", phoneCode: "+353", emoji: "🇮🇪", currencyCode: "EUR" },
  { name: "Israel", code: "IL", phoneCode: "+972", emoji: "🇮🇱", currencyCode: "ILS" },
  { name: "Italy", code: "IT", phoneCode: "+39", emoji: "🇮🇹", currencyCode: "EUR" },
  { name: "Jamaica", code: "JM", phoneCode: "+1-876", emoji: "🇯🇲", currencyCode: "JMD" },
  { name: "Japan", code: "JP", phoneCode: "+81", emoji: "🇯🇵", currencyCode: "JPY" },
  { name: "Jordan", code: "JO", phoneCode: "+962", emoji: "🇯🇴", currencyCode: "JOD" },
  { name: "Kazakhstan", code: "KZ", phoneCode: "+7", emoji: "🇰🇿", currencyCode: "KZT" },
  { name: "Kenya", code: "KE", phoneCode: "+254", emoji: "🇰🇪", currencyCode: "KES" },
  { name: "Kiribati", code: "KI", phoneCode: "+686", emoji: "🇰🇮", currencyCode: "AUD" },
  { name: "Kuwait", code: "KW", phoneCode: "+965", emoji: "🇰🇼", currencyCode: "KWD" },
  { name: "Kyrgyzstan", code: "KG", phoneCode: "+996", emoji: "🇰🇬", currencyCode: "KGS" },
  { name: "Laos", code: "LA", phoneCode: "+856", emoji: "🇱🇦", currencyCode: "LAK" },
  { name: "Latvia", code: "LV", phoneCode: "+371", emoji: "🇱🇻", currencyCode: "EUR" },
  { name: "Lebanon", code: "LB", phoneCode: "+961", emoji: "🇱🇧", currencyCode: "LBP" },
  { name: "Lesotho", code: "LS", phoneCode: "+266", emoji: "🇱🇸", currencyCode: "LSL" },
  { name: "Liberia", code: "LR", phoneCode: "+231", emoji: "🇱🇷", currencyCode: "LRD" },
  { name: "Libya", code: "LY", phoneCode: "+218", emoji: "🇱🇾", currencyCode: "LYD" },
  { name: "Liechtenstein", code: "LI", phoneCode: "+423", emoji: "🇱🇮", currencyCode: "CHF" },
  { name: "Lithuania", code: "LT", phoneCode: "+370", emoji: "🇱🇹", currencyCode: "EUR" },
  { name: "Luxembourg", code: "LU", phoneCode: "+352", emoji: "🇱🇺", currencyCode: "EUR" },
  { name: "Madagascar", code: "MG", phoneCode: "+261", emoji: "🇲🇬", currencyCode: "MGA" },
  { name: "Malawi", code: "MW", phoneCode: "+265", emoji: "🇲🇼", currencyCode: "MWK" },
  { name: "Malaysia", code: "MY", phoneCode: "+60", emoji: "🇲🇾", currencyCode: "MYR" },
  { name: "Maldives", code: "MV", phoneCode: "+960", emoji: "🇲🇻", currencyCode: "MVR" },
  { name: "Mali", code: "ML", phoneCode: "+223", emoji: "🇲🇱", currencyCode: "XOF" },
  { name: "Malta", code: "MT", phoneCode: "+356", emoji: "🇲🇹", currencyCode: "EUR" },
  { name: "Marshall Islands", code: "MH", phoneCode: "+692", emoji: "🇲🇭", currencyCode: "USD" },
  { name: "Mauritania", code: "MR", phoneCode: "+222", emoji: "🇲🇷", currencyCode: "MRU" },
  { name: "Mauritius", code: "MU", phoneCode: "+230", emoji: "🇲🇺", currencyCode: "MUR" },
  { name: "Mexico", code: "MX", phoneCode: "+52", emoji: "🇲🇽", currencyCode: "MXN" },
  { name: "Micronesia", code: "FM", phoneCode: "+691", emoji: "🇫🇲", currencyCode: "USD" },
  { name: "Moldova", code: "MD", phoneCode: "+373", emoji: "🇲🇩", currencyCode: "MDL" },
  { name: "Monaco", code: "MC", phoneCode: "+377", emoji: "🇲🇨", currencyCode: "EUR" },
  { name: "Mongolia", code: "MN", phoneCode: "+976", emoji: "🇲🇳", currencyCode: "MNT" },
  { name: "Montenegro", code: "ME", phoneCode: "+382", emoji: "🇲🇪", currencyCode: "EUR" },
  { name: "Morocco", code: "MA", phoneCode: "+212", emoji: "🇲🇦", currencyCode: "MAD" },
  { name: "Mozambique", code: "MZ", phoneCode: "+258", emoji: "🇲🇿", currencyCode: "MZN" },
  { name: "Myanmar", code: "MM", phoneCode: "+95", emoji: "🇲🇲", currencyCode: "MMK" },
  { name: "Namibia", code: "NA", phoneCode: "+264", emoji: "🇳🇦", currencyCode: "NAD" },
  { name: "Nauru", code: "NR", phoneCode: "+674", emoji: "🇳🇷", currencyCode: "AUD" },
  { name: "Nepal", code: "NP", phoneCode: "+977", emoji: "🇳🇵", currencyCode: "NPR" },
  { name: "Netherlands", code: "NL", phoneCode: "+31", emoji: "🇳🇱", currencyCode: "EUR" },
  { name: "New Zealand", code: "NZ", phoneCode: "+64", emoji: "🇳🇿", currencyCode: "NZD" },
  { name: "Nicaragua", code: "NI", phoneCode: "+505", emoji: "🇳🇮", currencyCode: "NIO" },
  { name: "Niger", code: "NE", phoneCode: "+227", emoji: "🇳🇪", currencyCode: "XOF" },
  { name: "Nigeria", code: "NG", phoneCode: "+234", emoji: "🇳🇬", currencyCode: "NGN" },
  { name: "North Korea", code: "KP", phoneCode: "+850", emoji: "🇰🇵", currencyCode: "KPW" },
  { name: "North Macedonia", code: "MK", phoneCode: "+389", emoji: "🇲🇰", currencyCode: "MKD" },
  { name: "Norway", code: "NO", phoneCode: "+47", emoji: "🇳🇴", currencyCode: "NOK" },
  { name: "Oman", code: "OM", phoneCode: "+968", emoji: "🇴🇲", currencyCode: "OMR" },
  { name: "Pakistan", code: "PK", phoneCode: "+92", emoji: "🇵🇰", currencyCode: "PKR" },
  { name: "Palau", code: "PW", phoneCode: "+680", emoji: "🇵🇼", currencyCode: "USD" },
  { name: "Palestine", code: "PS", phoneCode: "+970", emoji: "🇵🇸", currencyCode: "ILS" },
  { name: "Panama", code: "PA", phoneCode: "+507", emoji: "🇵🇦", currencyCode: "PAB" },
  { name: "Papua New Guinea", code: "PG", phoneCode: "+675", emoji: "🇵🇬", currencyCode: "PGK" },
  { name: "Paraguay", code: "PY", phoneCode: "+595", emoji: "🇵🇾", currencyCode: "PYG" },
  { name: "Peru", code: "PE", phoneCode: "+51", emoji: "🇵🇪", currencyCode: "PEN" },
  { name: "Philippines", code: "PH", phoneCode: "+63", emoji: "🇵🇭", currencyCode: "PHP" },
  { name: "Poland", code: "PL", phoneCode: "+48", emoji: "🇵🇱", currencyCode: "PLN" },
  { name: "Portugal", code: "PT", phoneCode: "+351", emoji: "🇵🇹", currencyCode: "EUR" },
  { name: "Puerto Rico", code: "PR", phoneCode: "+1-787", emoji: "🇵🇷", currencyCode: "USD" },
  { name: "Qatar", code: "QA", phoneCode: "+974", emoji: "🇶🇦", currencyCode: "QAR" },
  { name: "Romania", code: "RO", phoneCode: "+40", emoji: "🇷🇴", currencyCode: "RON" },
  { name: "Russia", code: "RU", phoneCode: "+7", emoji: "🇷🇺", currencyCode: "RUB" },
  { name: "Rwanda", code: "RW", phoneCode: "+250", emoji: "🇷🇼", currencyCode: "RWF" },
  { name: "Saint Kitts and Nevis", code: "KN", phoneCode: "+1-869", emoji: "🇰🇳", currencyCode: "XCD" },
  { name: "Saint Lucia", code: "LC", phoneCode: "+1-758", emoji: "🇱🇨", currencyCode: "XCD" },
  { name: "Saint Vincent and the Grenadines", code: "VC", phoneCode: "+1-784", emoji: "🇻🇨", currencyCode: "XCD" },
  { name: "Samoa", code: "WS", phoneCode: "+685", emoji: "🇼🇸", currencyCode: "WST" },
  { name: "San Marino", code: "SM", phoneCode: "+378", emoji: "🇸🇲", currencyCode: "EUR" },
  { name: "Sao Tome and Principe", code: "ST", phoneCode: "+239", emoji: "🇸🇹", currencyCode: "STN" },
  { name: "Saudi Arabia", code: "SA", phoneCode: "+966", emoji: "🇸🇦", currencyCode: "SAR" },
  { name: "Senegal", code: "SN", phoneCode: "+221", emoji: "🇸🇳", currencyCode: "XOF" },
  { name: "Serbia", code: "RS", phoneCode: "+381", emoji: "🇷🇸", currencyCode: "RSD" },
  { name: "Seychelles", code: "SC", phoneCode: "+248", emoji: "🇸🇨", currencyCode: "SCR" },
  { name: "Sierra Leone", code: "SL", phoneCode: "+232", emoji: "🇸🇱", currencyCode: "SLE" },
  { name: "Singapore", code: "SG", phoneCode: "+65", emoji: "🇸🇬", currencyCode: "SGD" },
  { name: "Slovakia", code: "SK", phoneCode: "+421", emoji: "🇸🇰", currencyCode: "EUR" },
  { name: "Slovenia", code: "SI", phoneCode: "+386", emoji: "🇸🇮", currencyCode: "EUR" },
  { name: "Solomon Islands", code: "SB", phoneCode: "+677", emoji: "🇸🇧", currencyCode: "SBD" },
  { name: "Somalia", code: "SO", phoneCode: "+252", emoji: "🇸🇴", currencyCode: "SOS" },
  { name: "South Africa", code: "ZA", phoneCode: "+27", emoji: "🇿🇦", currencyCode: "ZAR" },
  { name: "South Korea", code: "KR", phoneCode: "+82", emoji: "🇰🇷", currencyCode: "KRW" },
  { name: "South Sudan", code: "SS", phoneCode: "+211", emoji: "🇸🇸", currencyCode: "SSP" },
  { name: "Spain", code: "ES", phoneCode: "+34", emoji: "🇪🇸", currencyCode: "EUR" },
  { name: "Sri Lanka", code: "LK", phoneCode: "+94", emoji: "🇱🇰", currencyCode: "LKR" },
  { name: "Sudan", code: "SD", phoneCode: "+249", emoji: "🇸🇩", currencyCode: "SDG" },
  { name: "Suriname", code: "SR", phoneCode: "+597", emoji: "🇸🇷", currencyCode: "SRD" },
  { name: "Sweden", code: "SE", phoneCode: "+46", emoji: "🇸🇪", currencyCode: "SEK" },
  { name: "Switzerland", code: "CH", phoneCode: "+41", emoji: "🇨🇭", currencyCode: "CHF" },
  { name: "Syria", code: "SY", phoneCode: "+963", emoji: "🇸🇾", currencyCode: "SYP" },
  { name: "Taiwan", code: "TW", phoneCode: "+886", emoji: "🇹🇼", currencyCode: "TWD" },
  { name: "Tajikistan", code: "TJ", phoneCode: "+992", emoji: "🇹🇯", currencyCode: "TJS" },
  { name: "Tanzania", code: "TZ", phoneCode: "+255", emoji: "🇹🇿", currencyCode: "TZS" },
  { name: "Thailand", code: "TH", phoneCode: "+66", emoji: "🇹🇭", currencyCode: "THB" },
  { name: "Togo", code: "TG", phoneCode: "+228", emoji: "🇹🇬", currencyCode: "XOF" },
  { name: "Tonga", code: "TO", phoneCode: "+676", emoji: "🇹🇴", currencyCode: "TOP" },
  { name: "Trinidad and Tobago", code: "TT", phoneCode: "+1-868", emoji: "🇹🇹", currencyCode: "TTD" },
  { name: "Tunisia", code: "TN", phoneCode: "+216", emoji: "🇹🇳", currencyCode: "TND" },
  { name: "Turkey", code: "TR", phoneCode: "+90", emoji: "🇹🇷", currencyCode: "TRY" },
  { name: "Turkmenistan", code: "TM", phoneCode: "+993", emoji: "🇹🇲", currencyCode: "TMT" },
  { name: "Tuvalu", code: "TV", phoneCode: "+688", emoji: "🇹🇻", currencyCode: "AUD" },
  { name: "Uganda", code: "UG", phoneCode: "+256", emoji: "🇺🇬", currencyCode: "UGX" },
  { name: "Ukraine", code: "UA", phoneCode: "+380", emoji: "🇺🇦", currencyCode: "UAH" },
  { name: "United Arab Emirates", code: "AE", phoneCode: "+971", emoji: "🇦🇪", currencyCode: "AED" },
  { name: "United Kingdom", code: "GB", phoneCode: "+44", emoji: "🇬🇧", currencyCode: "GBP" },
  { name: "United States", code: "US", phoneCode: "+1", emoji: "🇺🇸", currencyCode: "USD" },
  { name: "Uruguay", code: "UY", phoneCode: "+598", emoji: "🇺🇾", currencyCode: "UYU" },
  { name: "Uzbekistan", code: "UZ", phoneCode: "+998", emoji: "🇺🇿", currencyCode: "UZS" },
  { name: "Vanuatu", code: "VU", phoneCode: "+678", emoji: "🇻🇺", currencyCode: "VUV" },
  { name: "Vatican City", code: "VA", phoneCode: "+379", emoji: "🇻🇦", currencyCode: "EUR" },
  { name: "Venezuela", code: "VE", phoneCode: "+58", emoji: "🇻🇪", currencyCode: "VES" },
  { name: "Vietnam", code: "VN", phoneCode: "+84", emoji: "🇻🇳", currencyCode: "VND" },
  { name: "Yemen", code: "YE", phoneCode: "+967", emoji: "🇾🇪", currencyCode: "YER" },
  { name: "Zambia", code: "ZM", phoneCode: "+260", emoji: "🇿🇲", currencyCode: "ZMW" },
  { name: "Zimbabwe", code: "ZW", phoneCode: "+263", emoji: "🇿🇼", currencyCode: "ZWL" },
];

Countries.forEach((c) => {
  c.continent = CONTINENT_BY_CODE[c.code];
});

const norm = (s: string) => s.trim().toLowerCase();

const NAME_TO_CODE: Record<string, string> = {
  ...Object.fromEntries(Countries.map((c) => [norm(c.name), c.code])),
  ...COUNTRY_NAME_ALIASES,
};

/** Continent for a country name ("Estonia") or ISO code ("EE"). Null if unknown. */
export function getContinent(country?: string | null): Continent | null {
  if (!country) return null;
  const key = country.trim();
  const byCode = CONTINENT_BY_CODE[key.toUpperCase()];
  if (key.length === 2 && byCode) return byCode;
  const code = NAME_TO_CODE[norm(key)];
  return code ? CONTINENT_BY_CODE[code] ?? null : null;
}

/** True when both countries are on the same continent. */
export function sameContinent(a?: string | null, b?: string | null): boolean {
  const ca = getContinent(a);
  return !!ca && ca === getContinent(b);
}

/** Every country on a continent. */
export function getCountriesByContinent(continent: Continent): CountryInfo[] {
  return Countries.filter((c) => c.continent === continent);
}

export default Countries;

/** Look up a country's currency by ISO country code, e.g. "KE" -> KES */
export function getCurrencyForCountry(countryCode: string): CurrencyItem | undefined {
  const country = Countries.find(c => c.code === countryCode);
  if (!country) return undefined;
  return CURRENCIES.find(cur => cur.code === country.currencyCode);
}